import { api } from "./client";

/**
 * Модулът, в който се е случило действието. Извежда се в БЕКЕНДА от първия
 * сегмент на пътя (`app/features/audit_logs/classify.py`) и пътува в реда —
 * фронтендът само превежда стойността до етикет.
 */
export type AuditLogModule =
  | "ordering"
  | "cash"
  | "reports"
  | "invoicing"
  | "inventory"
  | "configuration"
  | "access"
  | "system";

/**
 * Колко тежи действието: мести ли пари, променя ли настройка, друга промяна
 * по данните, или е само четене. Също се смята в бекенда.
 */
export type AuditLogImpact = "money" | "settings" | "data" | "read";

export interface AuditLogEntry {
  id: number;
  timestamp: string;
  user_id: number | null;
  user_name: string | null;
  bill_id: number | null;
  table_name: string | null;
  operation: string;
  method: string;
  path: string;
  status_code: number;
  duration_ms: number;
  module: AuditLogModule;
  impact: AuditLogImpact;
  /** HTTP 2xx, но бонът не е излязъл (`fiscal_result.status === "failed"`). */
  fiscal_failed: boolean;
}

export interface AuditLogDetail extends AuditLogEntry {
  request_headers: Record<string, string> | null;
  request_query: Record<string, string> | null;
  request_body: unknown;
  response_body: unknown;
}

export interface AuditLogListResponse {
  items: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuditLogFilterOptions {
  operations: string[];
  methods: string[];
  users: Array<{ id: number; name: string }>;
}

export type AuditLogStatusClass = "2xx" | "4xx" | "5xx";

export interface AuditLogListParams {
  search?: string;
  operation?: string[];
  method?: string[];
  user_id?: number[];
  bill_id?: number;
  /** ТОЧНО име на маса — „1" не влачи „11". Търсачката е другото място. */
  table_name?: string;
  module?: AuditLogModule[];
  impact?: AuditLogImpact[];
  status_class?: AuditLogStatusClass[];
  /** HTTP грешка ИЛИ непечатан бон при 2xx. */
  only_problems?: boolean;
  min_duration_ms?: number;
  max_duration_ms?: number;
  start_date?: string;
  end_date?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

/** Едно деление на лентата с активността. */
export interface AuditLogBucket {
  start: string;
  count: number;
  problems: number;
}

export interface AuditLogModuleStat {
  module: AuditLogModule;
  count: number;
  problems: number;
}

export interface AuditLogUserStat {
  user_id: number | null;
  user_name: string | null;
  count: number;
  problems: number;
}

/** Обобщението за периода — смята се в базата по СЪЩИТЕ филтри като списъка. */
export interface AuditLogStats {
  total: number;
  problems: number;
  fiscal_failed: number;
  reads: number;
  writes: number;
  money: number;
  settings: number;
  users: number;
  slowest_ms: number;
  avg_ms: number;
  first_at: string | null;
  last_at: string | null;
  bucket: "hour" | "day";
  buckets: AuditLogBucket[];
  by_module: AuditLogModuleStat[];
  by_user: AuditLogUserStat[];
}

/** Масивите излизат като `?method=POST&method=PUT` — BE-то очаква точно това. */
const arrayParams = { paramsSerializer: { indexes: null } } as const;

export async function fetchAuditLogs(
  params: AuditLogListParams,
): Promise<AuditLogListResponse> {
  const res = await api.get<AuditLogListResponse>("/audit-logs", {
    params,
    ...arrayParams,
  });
  return res.data;
}

export async function fetchAuditLogFilters(params: {
  start_date?: string;
  end_date?: string;
}): Promise<AuditLogFilterOptions> {
  const res = await api.get<AuditLogFilterOptions>("/audit-logs/filters", {
    params,
  });
  return res.data;
}

export async function fetchAuditLogStats(
  params: AuditLogListParams,
): Promise<AuditLogStats> {
  const res = await api.get<AuditLogStats>("/audit-logs/stats", {
    params,
    ...arrayParams,
  });
  return res.data;
}

export async function fetchAuditLogDetail(id: number): Promise<AuditLogDetail> {
  const res = await api.get<AuditLogDetail>(`/audit-logs/${id}`);
  return res.data;
}
